import {Component, Input, OnDestroy, OnInit, Optional, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {FormGroup} from '@angular/forms';
import {TranslationEventService, TranslationService, DATE_FORMAT_YMD, Translatable} from '@ngmedax/translation';
import {RegistryService} from '@ngmedax/registry';
import {ConfigService} from '@ngmedax/config';
import {languages} from '@ngmedax/cloud-translator';
import {LayoutService} from '@ngmedax/layout';
import {firstValueFrom} from 'rxjs';
import {take} from 'rxjs/operators';

import {MasterDataLayoutService, MasterDataLayout, MasterDataFieldPosition, CustomFieldDefinition} from '@ngmedax/master-data';
import {QuestionnaireSelectComponent} from '@ngmedax/questionnaire-select';

import {PatientService} from '../services/patient.service';
import {ThiemeCapService} from '../services/thieme-cap.service';
import {SurveyService} from '../services/survey.service';
import {ClipboardService} from '../services/clipboard.service';
import {PatientDateService} from '../services/patient-date.service';
import {CustomFieldsManagerService} from '../services/custom-fields-manager.service';
import {PatientFormBuilderService} from '../services/patient-form-builder.service';
import {MobileLinkService} from '../services/mobile-link.service';
import {configKeys} from '../patient.config-keys';
import {TRANSLATION_CRUD_SCOPE} from '../../../constants';
import {Patient} from '../../../types';
import {KEYS} from '../../../translation-keys';


declare const window: any;

// hack to inject decorator declarations. must occur before class declaration!
export interface PatientCrudComponent extends Translatable {}

/**
 * Component for creating and editing patients with questionnaire assignment.
 * Orchestrates form management, QR code display, and patient CRUD operations.
 */
@Component({
  selector: 'app-patient-crud',
  templateUrl: './patient-crud.component.html',
  styleUrls: ['./patient-crud.component.css']
})
@Translatable({scope: TRANSLATION_CRUD_SCOPE, keys: KEYS})
export class PatientCrudComponent implements OnInit, OnDestroy {
  /**
   * Subscriptions for cleanup.
   */
  private subscriptions: Subscription[] = [];

  /**
   * Should we allow field population via query string?
   */
  @Input() public allowFormPopulationViaQueryString = false;

  /**
   * Patient form.
   */
  public patientForm: FormGroup;

  /**
   * Custom fields form.
   */
  public customFieldsForm: FormGroup;

  /**
   * Selected questionnaire IDs.
   */
  public selectedQuestionnaires: string[] = [];

  /**
   * Selected workflow ID.
   */
  public selectedWorkflow: string = null;

  /**
   * Current locale.
   */
  public locale = 'de_DE';

  /**
   * Should we hide the patient form?
   */
  public hideForm = false;

  /**
   * Feature configuration.
   */
  public feature: any = {patient: {app: {mail: false, link: {send: false, copy: false, open: false}}, anonymous: false}};

  /**
   * Should we show a button to close the window?
   */
  public showCloseWinBtn = false;

  /**
   * Is survey mail configured on the server?
   */
  public isSurveyMailConfigured = false;

  /**
   * Survey limits.
   */
  public surveyLimit = {current: 0, max: 0, reached: false};

  /**
   * Thieme cap state. Only rendered when loaded is true
   * (thieme feature licensed and cap fetch succeeded).
   */
  public thiemeCap = {used: 0, limit: 0, remaining: 0, loaded: false};

  /**
   * Questionnaire select component. Used to check if the current selection contains thieme questionnaires.
   */
  @ViewChild(QuestionnaireSelectComponent) private questionnaireSelect: QuestionnaireSelectComponent;

  /**
   * QR code mode (1 = mobile, 2 = browser).
   */
  public qrMode = 1;

  /**
   * QR code target.
   */
  public qrTarget = 'mobile';

  /**
   * Current link for browser QR code.
   */
  public qrFillLink = '';

  /**
   * Whether form has unsaved changes.
   */
  public dirty = true;

  /**
   * Whether user toggle is disabled.
   */
  public disableUserToggle = false;

  /**
   * Overlay mode flag.
   */
  public overlayMode: boolean = false;

  /**
   * Available languages.
   */
  public languages = languages;

  /**
   * Anonymous allocation mode flag.
   */
  private anonymousAllocation = false;

  /**
   * Pseudonymous allocation mode flag.
   */
  private pseudonymousAllocation = false;

  /**
   * Custom field definitions.
   */
  public customFieldDefinitions: CustomFieldDefinition[] = [];

  /**
   * Master data layout configuration.
   */
  public masterDataLayout: MasterDataLayout | null = null;

  /**
   * Injects dependencies.
   */
  public constructor(
    private activatedRoute: ActivatedRoute,
    private patientService: PatientService,
    private thiemeCapService: ThiemeCapService,
    private surveyService: SurveyService,
    private clipboardService: ClipboardService,
    private configService: ConfigService,
    private registryService: RegistryService,
    private layoutService: LayoutService,
    private masterDataLayoutService: MasterDataLayoutService,
    private patientDateService: PatientDateService,
    private customFieldsManager: CustomFieldsManagerService,
    private patientFormBuilder: PatientFormBuilderService,
    private mobileLinkService: MobileLinkService,
    @Optional() private translationEvents: TranslationEventService,
    @Optional() private translationService: TranslationService,
  ) {
    if (location.pathname.match(/assign/)) {
      this.hideForm = true;
    }
  }

  /**
   * Checks if a custom layout is configured.
   */
  public get hasCustomLayout(): boolean {
    return this.masterDataLayoutService.hasCustomLayout(this.masterDataLayout);
  }

  /**
   * Gets the effective layout (custom or default).
   */
  public get effectiveLayout(): MasterDataLayout {
    return this.masterDataLayoutService.getEffectiveLayout(this.masterDataLayout);
  }

  public ngOnInit() {
    this.disableUserToggle = !(this.configService.get('feature.patient.new') == true);
    this.translationService && (this.locale = this.translationService.getLocale());
    this.feature.patient = this.configService.get('feature.patient') || this.feature.patient;

    // create forms using services
    this.patientForm = this.patientFormBuilder.createPatientForm();
    this.customFieldsForm = this.customFieldsManager.createEmptyForm();

    // load custom field definitions and layout
    this.loadCustomFieldDefinitions();

    this.patientService.isSurveyMailConfigured()
      .then(isConfigured => this.isSurveyMailConfigured = isConfigured)
      .catch(error => console.error(error));

    // handle locale changes
    this.translationEvents && this.translationEvents.onLocaleChanged().subscribe(() => {
      const fromLocale = this.locale;
      const toLocale = this.translationService.getLocale();
      const date = this.patientForm.get('birthDate').value;
      const converted = this.patientDateService.convertLocale(date, fromLocale, toLocale);
      this.patientForm.get('birthDate').setValue(converted);
      this.locale = toLocale;
      this.renderDateFields();
    });

    const init = async () => {
      const qPatient = await this.getPatientByQueryParams();

      if (qPatient) {
        this.dirty = false;
        this.renderPatient(qPatient);
      }

      this.activatedRoute.queryParams.subscribe((params: any) => params.closeWinBtn && (this.showCloseWinBtn = true));

      location.pathname.match(/anonymous/) && this.onToggleAnonymous(true);
      location.pathname.match(/pseudonymous/) && this.onTogglePseudonymous(true);

      const firstParams = await firstValueFrom(this.activatedRoute.params.pipe(take(1)));
      firstParams.id && await this.loadPatient(firstParams.id, qPatient);

      this.renderDateFields();
      const license = this.registryService.get('license');

      if (license && license.constraint && license.constraint.numSurveysPerMonth) {
        this.surveyLimit.current = await this.patientService.getNumSubmittedSurveysForCurrentMonth();
        this.surveyLimit.max = license.constraint.numSurveysPerMonth;
        this.surveyLimit.reached = this.surveyLimit.current >= this.surveyLimit.max;
        (<any>this.surveyLimit.current) += '';
      }

      this.loadThiemeCap();
      this.applyQuotaPreviewParams();
    };

    init().then(() => {
      this.qrTarget = this.configService.get('patient.qr.target');
      this.qrTarget && this.qrTarget === 'mobile' && (this.onToggleQr(1));
      this.qrTarget && this.qrTarget === 'browser' && (this.onToggleQr(2));
    }).catch(error => console.error(error));

    // trigger import if on import route
    if (location && location.pathname && location.pathname.match(/\/module\/patient\/import$/)) {
      setTimeout(() => this.onTriggerImport(), 0);
    }
  }

  public ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  /**
   * Creates a jump-in code for the patient.
   */
  public async onCreateJumpInCode() {
    try {
      const patientFormValues: Patient = this.patientForm.value;
      const patient = await this.patientService.loadPatient(patientFormValues.uid);
      const survey = await this.surveyService.createSurvey(patient, patient.locale || this.locale);
      const surveyCode = await this.surveyService.createSurveyCode(survey.uid);
      const code = surveyCode.code;
      this.clipboardService.copy(code);
      alert(this._(KEYS.CRUD.SUCCESSFULLY_CREATED_JUMP_IN_CODE) + `: ${code}`);
    } catch (error) {
      console.error(error);
      alert(this._(KEYS.CRUD.ERROR_CREATING_JUMP_IN_CODE));
    }
  }

  public onWorkflowSelect(workflowId: string) {
    this.selectedQuestionnaires = [];
    this.selectedWorkflow = workflowId;
  }

  public onQuestionnairesSelect(questionnaires: string[]) {
    this.selectedQuestionnaires = questionnaires;
    this.dirty = true;
  }

  /**
   * Opens mobile frontend in new window.
   */
  public async onFillStart() {
    this.patientForm.valid && await this.savePatient();
    const link = this.getMobileFrontendLink();
    this.mobileLinkService.openInNewWindow(link);
  }

  /**
   * Copies mobile frontend link to clipboard.
   */
  public async onFillCopyClipboard() {
    this.patientForm.valid && await this.savePatient();
    const link = this.getMobileFrontendLink();
    this.clipboardService.copy(link);
  }

  /**
   * Opens mail client with link.
   */
  public async onSendMail() {
    this.patientForm.valid && await this.savePatient();
    const email = this.patientForm.get('email')?.value || '';
    const bodyText = `${this._(KEYS.CRUD.MAIL_BODY_FORM_TEXT)}\n${this.getMobileFrontendLink(false)}`;
    this.mobileLinkService.openMailClient(email, bodyText);
  }

  /**
   * Toggles anonymous mode.
   */
  public onToggleAnonymous(setTrue = false) {
    this.anonymousAllocation = setTrue ? true : !this.anonymousAllocation;
    this.patientFormBuilder.resetForAnonymous(this.patientForm, this.anonymousAllocation);
  }

  /**
   * Toggles pseudonymous mode.
   */
  public onTogglePseudonymous(setTrue = false) {
    this.pseudonymousAllocation = setTrue ? true : !this.pseudonymousAllocation;
    this.patientFormBuilder.resetForPseudonymous(this.patientForm, this.pseudonymousAllocation);
  }

  /**
   * Toggles QR code mode.
   */
  public onToggleQr(qrMode: number) {
    this.qrMode = qrMode;
    this.qrFillLink = this.getMobileFrontendLink();
  }

  /**
   * Handles QR mode change from child component.
   */
  public onQrModeChanged(mode: number) {
    this.onToggleQr(mode);
  }

  /**
   * Form submit action.
   */
  public onSubmit() {
    // mark all custom fields as touched
    this.customFieldsManager.markAllAsTouched(this.customFieldsForm);

    // check custom fields validity
    if (this.customFieldsForm && this.customFieldsForm.invalid) {
      return;
    }

    // hard block: no assignment of thieme questionnaires without remaining quota
    // (analogous to the monthly survey limit, decided by benny 2026-07-22)
    if (this.thiemeCap.loaded && this.thiemeCap.remaining <= 0 && this.hasThiemeQuestionnaireSelected()) {
      alert(this._(KEYS.CRUD.THIEME_NO_QUOTA));
      return;
    }

    this.savePatient()
      .then(() => setTimeout(() => {
        this.selectedQuestionnaires.length && !this.overlayMode && alert(this._(KEYS.CRUD.SAVED_QR_CODE_HINT));
      }, 700))
      .catch(error => console.log(error));
  }

  /**
   * Loads patient by ID.
   */
  public async loadPatient(patientId: string, qPatient: any) {
    try {
      this.layoutService.showPreloader();
      const patient = await this.patientService.loadPatient(patientId);

      if (!patient) {
        alert(this._(KEYS.CRUD.FOUND_NO_PATIENT_BY_ID) + ' ' + patientId);
        return;
      }

      this.dirty = false;
      this.renderPatient(patient);

      if (qPatient) {
        this.renderPatient(qPatient);
      }
      this.layoutService.hidePreloader();
    } catch (error) {
      console.error(error);
      this.layoutService.hidePreloader();
      alert(this._(KEYS.CRUD.ERROR_LOADING_PATIENT));
    }
  }

  /**
   * Returns local date format.
   */
  public getLocalDateFormat(): string {
    return this.patientDateService.getLocalDateFormat(this.locale);
  }

  /**
   * Returns local date format mask.
   */
  public getLocalDateFormatMask(): string {
    return this.patientDateService.getLocalDateFormatMask(this.locale);
  }

  /**
   * Builds mobile frontend link.
   */
  public getMobileFrontendLink(encodeUri: boolean = false): string {
    const patientUid = this.patientForm.get('uid').value;
    const locale = this.patientForm.get('locale').value || this.locale;
    return this.mobileLinkService.buildMobileFrontendLink(patientUid, locale, encodeUri);
  }

  public objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  /**
   * Thieme cap values as strings for translation interpolation (falsy numbers would not be rendered).
   */
  public get thiemeCapVars(): {used: string, limit: string} {
    return {used: `${this.thiemeCap.used}`, limit: `${this.thiemeCap.limit}`};
  }

  /**
   * Display state of the survey limit pill: 'ok', 'warn' (10% or less remaining) or 'full'
   */
  public get surveyLimitState(): string {
    if (this.surveyLimit.reached) {
      return 'full';
    }

    const remaining = this.surveyLimit.max - Number(this.surveyLimit.current);
    return remaining <= this.surveyLimit.max * 0.1 ? 'warn' : 'ok';
  }

  /**
   * Display state of the thieme cap pill: 'ok', 'warn' (5 or less remaining) or 'full'
   */
  public get thiemeCapState(): string {
    if (this.thiemeCap.remaining <= 0) {
      return 'full';
    }

    return this.thiemeCap.remaining <= 5 ? 'warn' : 'ok';
  }

  public translateLanguage(language: string): string {
    return this.translationService ?
      this.translationService.translate({
        text: language,
        scope: ['cloudTranslatorModule', 'default']
      }) : language;
  }

  // layout helper methods delegated to service

  public getGridTemplateColumns(): string {
    return this.masterDataLayoutService.getGridTemplateColumns(this.effectiveLayout);
  }

  public getGridColumn(position: MasterDataFieldPosition): string {
    return this.masterDataLayoutService.getGridColumn(position);
  }

  public getGridRow(position: MasterDataFieldPosition): string {
    return this.masterDataLayoutService.getGridRow(position);
  }

  public isFieldVisible(fieldKey: string): boolean {
    return this.masterDataLayoutService.isFieldVisible(this.effectiveLayout, fieldKey);
  }

  public getCustomDefinition(key: string): CustomFieldDefinition | undefined {
    return this.customFieldsManager.getDefinition(this.customFieldDefinitions, key);
  }

  public range(n: number): number[] {
    return this.masterDataLayoutService.range(n);
  }

  // custom field validation helpers

  public isCustomFieldInvalid(key: string): boolean {
    return this.customFieldsManager.isFieldInvalid(this.customFieldsForm, key);
  }

  public getCustomFieldHint(def: CustomFieldDefinition): string {
    const control = this.customFieldsForm.get(def.key);
    const isRequiredError = control?.errors?.['required'];

    // use validation.hint if available
    if (def.validation?.hint) {
      return def.validation.hint;
    }

    // fallback to default messages
    return isRequiredError
      ? this._(KEYS.CRUD.CUSTOM_FIELD_REQUIRED)
      : this._(KEYS.CRUD.CUSTOM_FIELD_INVALID);
  }

  /**
   * Saves patient to API.
   */
  private async savePatient() {
    try {
      let patient: Patient = this.patientForm.value;

      // apply defaults for hidden fields if using custom layout
      if (this.hasCustomLayout) {
        const visibleFields = this.masterDataLayoutService.getVisibleStandardFields(this.effectiveLayout);
        patient = this.patientFormBuilder.applyDefaultsForHiddenFields(patient, visibleFields);
      }

      // prepare form values for save
      patient = this.patientFormBuilder.prepareForSave(patient);

      // convert date to MySQL format
      if (patient.birthDate) {
        patient.birthDate = this.patientDateService.toMySqlDate(patient.birthDate, this.locale);
      }

      patient.workflowId = this.selectedWorkflow || null;

      // add custom field values
      if (this.customFieldDefinitions.length > 0) {
        patient.custom = this.customFieldsManager.collectValues(this.customFieldsForm, this.customFieldDefinitions);
      }

      const {uid} = await this.patientService.savePatient(patient);
      let savedPatient = await this.patientService.loadPatient(uid);
      savedPatient.birthDate = patient.birthDate;

      await this.patientService.assignQuestionnaires(savedPatient, this.selectedQuestionnaires);
      savedPatient = await this.patientService.loadPatient(savedPatient.uid);
      this.dirty = false;

      this.renderPatient(savedPatient);
      alert(this._(KEYS.CRUD.SUCCESSFULLY_SAVED_PATIENT));

      // refresh the thieme cap display, so the user sees the current state after each assignment
      this.loadThiemeCap();
    } catch (error) {
      console.log(error);
      alert(this._(KEYS.CRUD.ERROR_SAVING_PATIENT));
    }
  }

  /**
   * Loads the thieme cap state when the thieme feature is licensed.
   * On errors the cap line stays hidden. Better no info than a wrong one.
   */
  private loadThiemeCap(): void {
    // quota preview overrides the display for testing - do not overwrite it with real data
    if (this.quotaPreviewActive) {
      return;
    }

    if (this.configService.get('feature.thieme') !== true) {
      return;
    }

    this.thiemeCapService.getCap()
      .then(cap => this.thiemeCap = {used: cap.used, limit: cap.limit, remaining: cap.remaining, loaded: true})
      .catch(error => console.warn('unable to load thieme cap state:', error));
  }

  /**
   * Returns true if the current selection contains at least one thieme questionnaire
   */
  private hasThiemeQuestionnaireSelected(): boolean {
    return !!this.questionnaireSelect && this.questionnaireSelect.hasThiemeSelected();
  }

  /**
   * True when quota preview query params are present (testing aid).
   */
  private get quotaPreviewActive(): boolean {
    const params = this.activatedRoute.snapshot?.queryParams || {};
    return !!(params.thiemeCapPreview || params.surveyLimitPreview);
  }

  /**
   * Testing aid: overrides the quota pill states via query params, so all display
   * states can be inspected without manipulating license or tracking data.
   * E.g: /module/patient/crud/new?surveyLimitPreview=95,100&thiemeCapPreview=997,1000
   */
  private applyQuotaPreviewParams(): void {
    const params = this.activatedRoute.snapshot?.queryParams || {};
    const parse = (value: any): number[] => `${value}`.split(',').map(v => parseInt(v, 10));

    if (params.surveyLimitPreview) {
      const [current, max] = parse(params.surveyLimitPreview);

      if (!isNaN(current) && !isNaN(max) && max > 0) {
        this.surveyLimit = {current, max, reached: current >= max};
      }
    }

    if (params.thiemeCapPreview) {
      const [used, limit] = parse(params.thiemeCapPreview);

      if (!isNaN(used) && !isNaN(limit) && limit > 0) {
        this.thiemeCap = {used, limit, remaining: Math.max(0, limit - used), loaded: true};
      }
    }
  }

  /**
   * Renders patient into form.
   */
  private renderPatient(patient: Patient) {
    Object.keys(patient).forEach(prop => {
      if (this.patientForm.contains(prop)) {
        let value = patient[prop];

        if (prop === 'birthDate' && value) {
          // mysql date format
          if (this.patientDateService.isMySqlFormat(value)) {
            value = this.patientDateService.fromMySqlDate(value, this.locale);
          }
          // german date format from query string
          if (this.patientDateService.isGermanFormat(value) && this.locale !== 'de_DE') {
            value = this.patientDateService.convertLocale(value, 'de_DE', this.locale);
          }
        }

        if (prop == 'prefill' && value == true) {
          value = patient.prefillOptional ? 'optional' : 'yes';
        }

        if (prop == 'prefill' && value == false) {
          value = 'no';
        }

        this.patientForm.get(prop).setValue(value);
      }
    });

    if (Array.isArray(patient.questionnaires)) {
      this.selectedQuestionnaires = patient.questionnaires.map(q => q.id);
    }

    if (patient.workflowId) {
      this.selectedWorkflow = patient.workflowId;
    }

    // render custom fields
    if (patient.custom) {
      this.customFieldsManager.renderToForm(this.customFieldsForm, patient.custom, this.customFieldDefinitions);
    }

    this.onToggleQr(this.qrMode);
  }

  /**
   * Gets patient from query params.
   */
  private async getPatientByQueryParams(): Promise<Patient> {
    const queryParams = this.activatedRoute.snapshot?.queryParams || null;

    if (!queryParams || (queryParams.id || queryParams.uid)) {
      return null;
    }

    const params = this.clone(queryParams);

    // set locale from query string
    if (params.locale && this.translationService) {
      await new Promise<void>((resolve) => setTimeout(() => resolve(), 10));
      this.translationService.setLocale(params.locale);
    }

    return queryParams !== null && Object.keys(queryParams).length !== 0 ? <Patient>this.clone(queryParams) : null;
  }

  /**
   * Renders date field masks.
   */
  private renderDateFields() {
    this.patientDateService.renderDateMask('birthDate', this.locale);
  }

  private clone(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Loads custom field definitions and layout.
   */
  private async loadCustomFieldDefinitions(): Promise<void> {
    try {
      const [definitions, layout] = await Promise.all([
        this.customFieldsManager.loadDefinitions(),
        this.masterDataLayoutService.loadLayout()
      ]);

      this.customFieldDefinitions = definitions;
      this.masterDataLayout = layout;
      this.customFieldsForm = this.customFieldsManager.buildForm(definitions);
      this.updateFormValidators();

      // clear validators for custom fields not visible in the layout
      const visibleCustomFields = this.masterDataLayoutService.getVisibleCustomFields(this.effectiveLayout);
      this.customFieldsManager.updateValidatorsForLayout(this.customFieldsForm, definitions, visibleCustomFields);
    } catch (error) {
      this.customFieldDefinitions = [];
      this.masterDataLayout = null;
    }
  }

  /**
   * Updates form validators based on layout.
   */
  private updateFormValidators(): void {
    if (!this.hasCustomLayout) {
      return;
    }

    const visibleFields = this.masterDataLayoutService.getVisibleStandardFields(this.effectiveLayout);
    this.patientFormBuilder.updateValidatorsForLayout(this.patientForm, visibleFields);
  }

  /**
   * Triggers ZIP import.
   */
  private onTriggerImport() {
    const input: HTMLInputElement = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip,application/zip';
    input.style.display = 'none';
    document.body.appendChild(input);

    const cleanup = () => document.body.removeChild(input);

    input.addEventListener('change', async () => {
      const file = input.files && input.files[0];
      if (!file) {
        cleanup();
        return;
      }

      try {
        this.layoutService.showPreloader();
        const res = await this.patientService.importSurveyZip(file, true);
        const clientId = res && (res.clientId || null);
        if (!clientId) {
          throw new Error('Missing clientId in response');
        }
        location.href = `/module/patient/crud/${clientId}`;
      } catch (error) {
        console.error(error);
        const err = <any>error;
        const apiMsg = (err && (err.error && (err.error.message || err.error.error || err.error || err.message))) || '';
        alert(`${this._(KEYS.CRUD.ERROR_IMPORTING_PATIENT)} ${apiMsg}`.trim());
      } finally {
        this.layoutService.hidePreloader();
        cleanup();
      }
    }, {once: true});

    input.click();
  }
}
